import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
  writeBatch,
  type DocumentReference,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type { InvestmentCaseChatMessage, InvestmentCaseChatThread } from '../types';
import { db } from '../lib/firebase';
import {
  COLLECTION_INVESTMENT_CASE_CHAT_MESSAGES,
  COLLECTION_INVESTMENT_CASE_CHAT_THREADS,
  ownedItemDocId,
} from '../lib/firestorePaths';
import { newId } from '../utils/id';

export const INVESTMENT_CASE_LEGACY_THREAD_ID = '__legacy__';

const LEGACY_THREAD_TITLE = 'Earlier conversation';
const FIRESTORE_BATCH_LIMIT = 500;

export type InvestmentCaseChatThreadsState = {
  threads: InvestmentCaseChatThread[];
  messages: InvestmentCaseChatMessage[];
  threadsLoading: boolean;
  messagesLoading: boolean;
  error: string | null;
  createThread: (title?: string) => Promise<InvestmentCaseChatThread>;
  deleteThread: (threadId: string) => Promise<void>;
};

function docToMessage(d: QueryDocumentSnapshot): InvestmentCaseChatMessage {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const parts = d.id.split('__');
  const fallbackId = parts.length > 1 ? parts.slice(1).join('__') : d.id;

  return {
    id: typeof x.id === 'string' && x.id.trim() ? x.id.trim() : fallbackId,
    userId: typeof x.userId === 'string' ? x.userId : '',
    caseId: typeof x.caseId === 'string' ? x.caseId : '',
    threadId: typeof x.threadId === 'string' && x.threadId.trim() ? x.threadId.trim() : undefined,
    role: x.role === 'assistant' ? 'assistant' : 'user',
    content: typeof x.content === 'string' ? x.content : '',
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
  };
}

function docToThread(d: QueryDocumentSnapshot): InvestmentCaseChatThread {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const parts = d.id.split('__');
  const fallbackId = parts.length > 1 ? parts.slice(1).join('__') : d.id;

  return {
    id: typeof x.id === 'string' && x.id.trim() ? x.id.trim() : fallbackId,
    userId: typeof x.userId === 'string' ? x.userId : '',
    caseId: typeof x.caseId === 'string' ? x.caseId : '',
    title: typeof x.title === 'string' && x.title.trim() ? x.title.trim() : 'New chat',
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
    updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : now,
  };
}

export function sortInvestmentCaseChatChronological(
  messages: InvestmentCaseChatMessage[]
): InvestmentCaseChatMessage[] {
  return [...messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function sortInvestmentCaseChatThreadsRecentFirst(
  threads: InvestmentCaseChatThread[]
): InvestmentCaseChatThread[] {
  return [...threads].sort((a, b) => {
    const byUpdated = b.updatedAt.localeCompare(a.updatedAt);
    if (byUpdated !== 0) return byUpdated;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

export function buildLegacyInvestmentCaseThread(
  userId: string,
  caseId: string,
  messages: InvestmentCaseChatMessage[]
): InvestmentCaseChatThread | null {
  const legacyMessages = messages.filter((message) => !message.threadId);
  if (legacyMessages.length === 0) return null;

  const sorted = sortInvestmentCaseChatChronological(legacyMessages);
  return {
    id: INVESTMENT_CASE_LEGACY_THREAD_ID,
    userId,
    caseId,
    title: LEGACY_THREAD_TITLE,
    createdAt: sorted[0].createdAt,
    updatedAt: sorted[sorted.length - 1].createdAt,
  };
}

export function filterInvestmentCaseThreadMessages(
  messages: InvestmentCaseChatMessage[],
  threadId: string | null
): InvestmentCaseChatMessage[] {
  if (!threadId) return [];
  if (threadId === INVESTMENT_CASE_LEGACY_THREAD_ID) {
    return messages.filter((message) => !message.threadId);
  }
  return messages.filter((message) => message.threadId === threadId);
}

export function useInvestmentCaseChatThreads(
  userId: string,
  caseId: string,
  activeThreadId: string | null
): InvestmentCaseChatThreadsState {
  const [storedThreads, setStoredThreads] = useState<InvestmentCaseChatThread[]>([]);
  const [allMessages, setAllMessages] = useState<InvestmentCaseChatMessage[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(Boolean(userId && caseId));
  const [messagesLoading, setMessagesLoading] = useState(Boolean(userId && caseId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId || !caseId) {
      setStoredThreads([]);
      setThreadsLoading(false);
      setError(null);
      return;
    }

    setThreadsLoading(true);

    const threadsQuery = query(
      collection(db, COLLECTION_INVESTMENT_CASE_CHAT_THREADS),
      where('userId', '==', userId),
      where('caseId', '==', caseId)
    );

    const unsubscribe = onSnapshot(
      threadsQuery,
      (snap) => {
        setStoredThreads(snap.docs.map(docToThread));
        setThreadsLoading(false);
      },
      (e) => {
        setError(e.message);
        setThreadsLoading(false);
      }
    );

    return unsubscribe;
  }, [userId, caseId]);

  useEffect(() => {
    if (!userId || !caseId) {
      setAllMessages([]);
      setMessagesLoading(false);
      return;
    }

    setMessagesLoading(true);

    const messagesQuery = query(
      collection(db, COLLECTION_INVESTMENT_CASE_CHAT_MESSAGES),
      where('userId', '==', userId),
      where('caseId', '==', caseId)
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snap) => {
        setAllMessages(snap.docs.map(docToMessage));
        setMessagesLoading(false);
      },
      (e) => {
        setError(e.message);
        setMessagesLoading(false);
      }
    );

    return unsubscribe;
  }, [userId, caseId]);

  const threads = useMemo(() => {
    const legacy = buildLegacyInvestmentCaseThread(userId, caseId, allMessages);
    return sortInvestmentCaseChatThreadsRecentFirst(
      legacy ? [...storedThreads, legacy] : storedThreads
    );
  }, [allMessages, caseId, storedThreads, userId]);

  const messages = useMemo(
    () =>
      sortInvestmentCaseChatChronological(
        filterInvestmentCaseThreadMessages(allMessages, activeThreadId)
      ),
    [activeThreadId, allMessages]
  );

  const createThread = useCallback(
    async (title = 'New chat'): Promise<InvestmentCaseChatThread> => {
      if (!userId) {
        throw new Error('Sign in to start a chat');
      }

      const now = new Date().toISOString();
      const thread: InvestmentCaseChatThread = {
        id: newId(),
        userId,
        caseId,
        title: title.trim() || 'New chat',
        createdAt: now,
        updatedAt: now,
      };

      await setDoc(
        doc(db, COLLECTION_INVESTMENT_CASE_CHAT_THREADS, ownedItemDocId(userId, thread.id)),
        thread
      );

      return thread;
    },
    [caseId, userId]
  );

  const deleteThread = useCallback(
    async (threadId: string): Promise<void> => {
      if (!userId) {
        throw new Error('Sign in to delete a chat');
      }

      const isLegacy = threadId === INVESTMENT_CASE_LEGACY_THREAD_ID;
      const messagesQuery = isLegacy
        ? query(
            collection(db, COLLECTION_INVESTMENT_CASE_CHAT_MESSAGES),
            where('userId', '==', userId),
            where('caseId', '==', caseId)
          )
        : query(
            collection(db, COLLECTION_INVESTMENT_CASE_CHAT_MESSAGES),
            where('userId', '==', userId),
            where('caseId', '==', caseId),
            where('threadId', '==', threadId)
          );

      const messageSnap = await getDocs(messagesQuery);
      const messageRefs = messageSnap.docs
        .filter((messageDoc) => !isLegacy || typeof messageDoc.data().threadId !== 'string')
        .map((messageDoc) => messageDoc.ref);

      const refsToDelete: DocumentReference[] = [...messageRefs];
      if (!isLegacy) {
        refsToDelete.push(
          doc(db, COLLECTION_INVESTMENT_CASE_CHAT_THREADS, ownedItemDocId(userId, threadId))
        );
      }

      for (let i = 0; i < refsToDelete.length; i += FIRESTORE_BATCH_LIMIT) {
        const batch = writeBatch(db);
        for (const ref of refsToDelete.slice(i, i + FIRESTORE_BATCH_LIMIT)) {
          batch.delete(ref);
        }
        await batch.commit();
      }
    },
    [caseId, userId]
  );

  return useMemo(
    () => ({
      threads,
      messages,
      threadsLoading,
      messagesLoading,
      error,
      createThread,
      deleteThread,
    }),
    [threads, messages, threadsLoading, messagesLoading, error, createThread, deleteThread]
  );
}
